import { useDispatch } from 'react-redux';
import {
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  createFilterOptions,
  debounce,
  Grid,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { isNil } from 'lodash-es';
import CancelIcon from '@mui/icons-material/Cancel';
import { actions } from '../../store/dux/product-placement-vpi-dux';
import { searchSeedCatalog } from '../../lib/api';

export default function AssignProductCell(props) {
  const { crop, field, sortOrder, assignment } = props;
  const dispatch = useDispatch();
  const filter = createFilterOptions();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { seedId, product, percent } = assignment;

  function updateProductAssignment(assignedProduct, assignedPercent) {
    dispatch(
      actions.setFieldProduct(
        field.aaManagedId,
        assignedProduct,
        'soy',
        assignedPercent,
        sortOrder,
      ),
    );
  }

  function unassignProduct() {
    dispatch(actions.unassignFieldProduct(field.aaManagedId, sortOrder));
  }

  function setPercent(value) {
    updateProductAssignment({ id: seedId, name: product }, value);
  }

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        if (isNil(searchTerm) || searchTerm.trimStart() === '') {
          setSearchResults([]);
          return;
        }

        setLoading(true);
        const results = await searchSeedCatalog(crop, query);
        setSearchResults(results);
        setLoading(false);
      }, 500),
    [crop, searchTerm],
  );

  useEffect(() => debouncedSearch(searchTerm), [searchTerm, debouncedSearch]);

  function handleProductAssignment(value) {
    if (typeof value === 'string' && value === product.name) return;
    if (typeof value === 'string') {
      updateProductAssignment({ name: value }, percent);
    } else if (value?.inputValue) {
      updateProductAssignment({ name: value.inputValue }, percent);
    } else {
      updateProductAssignment(value, percent);
    }
  }

  function handleCustomOption(options, params) {
    const filtered = filter(options, params);
    const { inputValue } = params;

    const isExisting = options.some((option) => inputValue === option.name);
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        name: inputValue,
        label: `Use custom "${inputValue}"`,
      });
    }

    return filtered;
  }

  return (
    <Grid container display={'flex'} alignItems="center">
      <Grid xs={7} item sx={{ pr: 1 }}>
        <Autocomplete
          freeSolo
          autoSelect
          data-testid={`assign-product-${field.aaManagedId}-${sortOrder}`}
          noOptionsText="No products found"
          loading={loading}
          value={product || null}
          onInputChange={(_, value) => setSearchTerm(value)}
          onChange={(_, value, reason) =>
            handleProductAssignment(value, reason)
          }
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={searchResults}
          getOptionDisabled={(option) => option.loading}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          filterOptions={handleCustomOption}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            if (option.inputValue) {
              return option.label;
            }
            return option.name;
          }}
          renderOption={renderOption}
          renderInput={(input) => (
            <TextField
              {...input}
              variant="outlined"
              InputProps={{
                ...input.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {input.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid xs={3} item>
        <TextField
          variant="outlined"
          data-testid={`assign-percent-${field.aaManagedId}-${sortOrder}`}
          name="percent"
          value={percent}
          onChange={(e) => setPercent(Number(e.target.value))}
        />
      </Grid>
      <Grid xs={2} item>
        {sortOrder > 0 && (
          <Button
            onClick={unassignProduct}
            data-testid={`remove-product-${field.aaManagedId}-${sortOrder}`}
            sx={{ pr: 3 }}
          >
            <CancelIcon color="dark" />
          </Button>
        )}
      </Grid>
    </Grid>
  );

  function renderOption(renderProps, option) {
    const brand = option?.brand && (
      <Typography variant="subtitle2" sx={{ marginLeft: 1 }}>
        {option.brand}
      </Typography>
    );
    return (
      <li {...renderProps} key={option.id || 'custom'}>
        {option.label || option.name}
        {brand}
      </li>
    );
  }
}
